<script setup>
import { ref, reactive, computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import powerAgentsApi from 'dashboard/api/powerAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ownedNumbersApi from 'dashboard/api/ownedWhatsappNumbers';
import WhatsappEmbeddedSignup from 'dashboard/routes/dashboard/settings/inbox/channels/WhatsappEmbeddedSignup.vue';
import SectionShell from './SectionShell.vue';
import TestLinkBlock from './TestLinkBlock.vue';
import { mensagemDoErro, codigoDoErro, dadosDoErro } from '../erros';

// O pai renderiza toda seção com `:agent`, e esta deixou de precisar dele: o único uso era o
// slug no código falso do site. Sem isto, o Vue despejaria `agent="[object Object]"` no DOM
// (o mesmo caso da SecretsSection).
defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const ctx = inject('powerAgentContext');
const store = useStore();

const inboxes = useMapGetter('inboxes/getAllInboxes');

// ── Block A — connected inbox state ──────────────────────────────
// PA-01 (D15): um agente serve N inboxes. A tela mostrava um só porque o backend devolvia
// um só — e `disconnect` desconectava "o inbox", sem dizer qual, deixando os demais
// ligados sem ninguém notar.
const connectedInboxes = ref([]);
const loadingChannel = ref(false);
const disconnecting = ref({});

// ── Escolha do tipo de inbox ─────────────────────────────────────
// A tela empilhava os quatro caminhos de uma vez (conectar existente, criar WhatsApp,
// Facebook, snippet do widget) e obrigava a ler tudo para achar um. Agora escolhe-se o
// TIPO primeiro e só o formulário daquele tipo aparece.
const tipo = ref(null);

// ── Block B — connect existing inbox state ───────────────────────
const connecting = ref(false);
// Inbox que já responde por outro agente: guarda o conflito para oferecer a reatribuição
// em vez de terminar num alerta sem saída.
const conflito = ref(null);
// Inboxes da conta que já respondem por outro agente (`bound_inboxes` do payload de canal).
const inboxesPresas = ref([]);

// ── Block C — create WhatsApp Cloud inbox state ──────────────────
const creating = ref(false);
const whatsappForm = reactive({
  inboxName: '',
  phone_number: '',
  phone_number_id: '',
  business_account_id: '',
  api_key: '',
});

// Meta rejects numbers that aren't E.164; validate before the round-trip so the
// user gets an inline error instead of an opaque 422 from the channel create.
const E164_RE = /^\+[1-9]\d{9,14}$/;
const phoneError = ref('');
const normalizedPhone = computed(() => {
  const p = whatsappForm.phone_number.trim();
  if (!p) return '';
  return p.startsWith('+') ? p : `+${p}`;
});
const phoneValid = computed(() => E164_RE.test(normalizedPhone.value));

// ── Block D — embedded Facebook flow gate ────────────────────────
const hasWhatsappAppId = computed(
  () =>
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
);

// ── Como conectar o WhatsApp ─────────────────────────────────────
// Eram três caminhos, e só um existia aqui. O botão "Conectar com o Facebook" não fazia
// Embedded Signup: era um `router.push` para `settings_inbox_new`, rota que nem aceita o
// parâmetro `page` que ele passava — caía na lista genérica de canais e o operador tinha
// que reencontrar o WhatsApp sozinho. Agora o Embedded Signup roda AQUI, pelo mesmo
// componente da tela nativa, e a inbox criada já entra ligada ao agente.
const metodoWa = ref('manual');

// Os números próprios da Powerbizz. A primeira versão disto era um link para a tela do
// Administrate (`/super_admin/owned_whatsapp_numbers`) — que é OUTRA aplicação, com
// `devise_for :super_admins` e sessão própria: quem clicava era mandado para um segundo
// login e não via número nenhum. Agora a lista vem pela API do próprio app.
const { isSuperAdmin } = useAdmin();
const numerosProprios = ref([]);
const carregandoProprios = ref(false);
const erroProprios = ref('');
const conectandoNumero = ref('');

// Vale mostrar o seletor só quando há mais de um caminho: com o app da Meta desconfigurado
// e usuário comum, "manual" é o único, e um seletor de uma opção é ruído.
// O rótulo sai daqui pronto porque `t()` com chave montada na hora é invisível para o
// extrator de i18n — a string existe e some do inventário sem ninguém notar.
const metodosWaDisponiveis = computed(() => {
  const todos = [];
  if (hasWhatsappAppId.value) {
    todos.push({ id: 'meta', rotulo: t('POWER_AGENTS.CHANNEL.METHOD_META') });
  }
  todos.push({ id: 'manual', rotulo: t('POWER_AGENTS.CHANNEL.METHOD_MANUAL') });
  if (isSuperAdmin.value) {
    todos.push({
      id: 'proprios',
      rotulo: t('POWER_AGENTS.CHANNEL.METHOD_OWNED'),
    });
  }
  return todos;
});

// O tipo de canal vira ícone: numa lista de inboxes o canal é a primeira coisa que se
// procura, e ler "Channel::Whatsapp" é pior que ver o ícone do WhatsApp.
const CANAIS = {
  whatsapp: {
    icone: 'i-lucide-message-circle',
    rotulo: 'WhatsApp',
    cor: 'text-[#25D366]',
  },
  web_widget: {
    icone: 'i-lucide-globe',
    rotulo: 'Website',
    cor: 'text-n-slate-11',
  },
  api: { icone: 'i-lucide-plug', rotulo: 'API', cor: 'text-n-slate-11' },
  google_chat: {
    icone: 'i-woot-google-chat',
    rotulo: 'Google Chat',
    cor: 'text-n-slate-11',
  },
};

// O que não é WhatsApp, site nem Google Chat cai em API. O Google Chat (GCHAT-01, 30/09/2026)
// caía também, e a caixa ligada aparecia como "API" com o ícone de tomada.
function chaveDoCanal(channelType) {
  const chave = String(channelType || '').toLowerCase();
  if (chave.includes('whatsapp')) return 'whatsapp';
  if (chave.includes('googlechat')) return 'google_chat';
  if (chave.includes('web')) return 'web_widget';
  return 'api';
}

function canalDe(channelType) {
  return CANAIS[chaveDoCanal(channelType)];
}

// Inbox já ligado a este agente não pode ser oferecido de novo na lista de conectar.
const inboxesDisponiveis = computed(() => {
  const ligados = new Set(connectedInboxes.value.map(i => Number(i.id)));
  return (inboxes.value || []).filter(i => !ligados.has(Number(i.id)));
});

// O código do site é o da CAIXA de site: o `web_widget_script` que o servidor monta com o
// `website_token` dela, o mesmo da tela de configuração da caixa. O que esta tela mostrava
// era um `pcw('init', { agentSlug })` montado aqui, que não abria widget nenhum — o SDK só
// sobe com o token de uma caixa, e o agente chega ao visitante por ser o bot dela.
// As caixas ligadas vêm do payload de canal, que não traz o script; quem traz é a lista de
// caixas da store, e por isso o cruzamento pelo id.
const caixasDeSite = computed(() => {
  const porId = new Map((inboxes.value || []).map(i => [Number(i.id), i]));
  return connectedInboxes.value
    .filter(i => chaveDoCanal(i.channel_type) === 'web_widget')
    .map(i => ({ ...i, script: porId.get(Number(i.id))?.webWidgetScript }));
});

// Quem segura cada inbox, por id. Vem do `bound_inboxes` do payload de canal, e existe para
// que a informação chegue ANTES do clique: até aqui, descobrir que a inbox estava presa
// exigia tentar conectar e tomar o conflito — ou seja, descobrir depois de decidir.
const donoPorInbox = computed(() => {
  const mapa = new Map();
  (inboxesPresas.value || []).forEach(p => {
    mapa.set(Number(p.inbox_id), p.agent_name || p.bot_name || '');
  });
  return mapa;
});

// Presa não é proibida: reatribuir continua sendo um caminho (`force`). Por isso o dono
// aparece no card em vez de a inbox sair da lista.
function donoDaInbox(inbox) {
  if (!donoPorInbox.value.has(Number(inbox.id))) return '';
  const dono = donoPorInbox.value.get(Number(inbox.id));
  return dono
    ? t('POWER_AGENTS.CHANNEL.INBOX_BOUND_TO', { name: dono })
    : t('POWER_AGENTS.CHANNEL.INBOX_BOUND_UNKNOWN');
}

// ── A escolha da inbox mora DENTRO do tipo ──────────────────────────────────────
// Antes havia um quadradinho "Inbox existente" ao lado dos tipos, e ele era uma pergunta
// de outra natureza: os outros três dizem QUE CANAL, e aquele dizia DE ONDE VEM. Quem
// tinha um número pronto clicava em "WhatsApp" — o caminho de CRIAR — e nao achava a inbox.
//
// Agora entrar num tipo mostra as inboxes DAQUELE canal como cards, mais um card de
// adicionar. A lista e o formulario deixam de ser dois caminhos concorrentes e viram um so.
const TIPO_PARA_CANAL = {
  whatsapp: 'whatsapp',
  web: 'web_widget',
  api: 'api',
  google_chat: 'google_chat',
};
// Os tres criam inbox aqui. O site entrou por ultimo: sem caixa de site nao ha codigo para
// colar, e mandar o operador criar a caixa em outra tela e voltar para ligar era o caminho
// em que o agente ficava fora do site.
const TIPOS_QUE_CRIAM = ['whatsapp', 'api', 'web'];

// O Google Chat só LIGA aqui: a caixa nasce nas caixas de entrada, pelo super admin, porque
// configurá-la pede o console do Google Cloud do cliente (GCHAT-01, D6). Até 30/09 ela caía no
// tipo API, e mapeá-la para o próprio canal a tiraria de lá; o tipo aparece para que ela
// continue podendo ser ligada, e só quando a conta tem uma caixa dessas.
const temGoogleChat = computed(() =>
  (inboxes.value || []).some(i => chaveDoCanal(i.channelType) === 'google_chat')
);

const inboxesDoTipo = computed(() => {
  const alvo = TIPO_PARA_CANAL[tipo.value];
  if (!alvo) return [];
  return inboxesDisponiveis.value.filter(
    i => chaveDoCanal(i.channelType) === alvo
  );
});

// Caixa de site só o super admin cria (`InboxPolicy#create_web_widget?`). O administrador, que vê
// esta seção desde 29/09/2026, liga e desliga a caixa de site que já existe e copia o código dela.
const podeCriar = computed(
  () =>
    TIPOS_QUE_CRIAM.includes(tipo.value) &&
    (tipo.value !== 'web' || isSuperAdmin.value)
);
const criandoNovo = ref(false);

// Os cards e a reatribuição servem a todos os tipos. No site terminam como a criação da
// caixa: no painel, com o código dela à vista (ver `createWebAndConnect`).
const ficaNoSite = computed(() => tipo.value === 'web');

// O formulario aparece quando pedido — ou direto, quando nao ha nenhuma inbox deste canal
// para escolher: obrigar um clique em "adicionar" numa grade de um card so e pedagio.
// No site, a caixa JA LIGADA tambem conta: quem abre o tipo com uma caixa de site no agente
// veio buscar o codigo dela, e o formulario de criar outra so abre a pedido.
const mostrandoCriacao = computed(() => {
  if (!podeCriar.value) return false;
  if (criandoNovo.value) return true;
  if (tipo.value === 'web' && caixasDeSite.value.length) return false;
  return !inboxesDoTipo.value.length;
});

// Trocar de tipo zera o que era do tipo anterior. Sem isto, um conflito levantado no
// WhatsApp continuaria na tela dentro do canal API, falando de uma inbox que nao esta ali.
watch(tipo, () => {
  criandoNovo.value = false;
  conflito.value = null;
  // Sem isto, sair do WhatsApp e voltar reabre no método que ficou da vez anterior.
  // O padrão é `manual` de propósito: o Embedded Signup ainda depende do Advanced Access
  // da Meta (app em análise desde 11/09/2026) e, para WABA de cliente terceiro, conclui na
  // tela e quebra depois, no register. Abrir no caminho que hoje funciona.
  metodoWa.value = 'manual';
});

const temInboxPresa = computed(() =>
  inboxesDoTipo.value.some(i => donoPorInbox.value.has(Number(i.id)))
);

// ── Block A — load current channel ───────────────────────────────
async function fetchChannel() {
  loadingChannel.value = true;
  try {
    const data = await powerAgentsApi.channel(ctx.accountId(), ctx.agentId());
    connectedInboxes.value = data.connected_inboxes || [];
    inboxesPresas.value = data.bound_inboxes || [];
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.CHANNEL.ERROR')));
  } finally {
    loadingChannel.value = false;
  }
}

async function disconnect(inboxId) {
  disconnecting.value = { ...disconnecting.value, [inboxId]: true };
  try {
    // Sempre com o id: desconectar sem dizer qual é ambíguo quando há mais de um.
    await powerAgentsApi.disconnectInbox(
      ctx.accountId(),
      ctx.agentId(),
      inboxId
    );
    useAlert(t('POWER_AGENTS.CHANNEL.DISCONNECTED'));
    await fetchChannel();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.CHANNEL.ERROR')));
  } finally {
    disconnecting.value = { ...disconnecting.value, [inboxId]: false };
  }
}

// ── Block B — connect an existing inbox ──────────────────────────
async function connect(inboxId, { force = false, ficar = false } = {}) {
  connecting.value = true;
  try {
    await powerAgentsApi.connectInbox(
      ctx.accountId(),
      ctx.agentId(),
      inboxId,
      force
    );
    useAlert(t('POWER_AGENTS.CHANNEL.CONNECTED'));
    conflito.value = null;
    // Volta para a grade de tipos: o passo terminou, e deixar o formulário aberto sugere
    // que ainda falta algo. Com `ficar`, a tela continua no tipo e só o formulário fecha.
    if (!ficar) tipo.value = null;
    await fetchChannel();
    // Fecha depois da lista nova: antes dela, a caixa recém-criada apareceria por um
    // instante como livre, e sem o código.
    criandoNovo.value = false;
  } catch (e) {
    // Inbox ocupado não é falha da pessoa nem erro passageiro: é uma decisão a tomar.
    // Vira um aviso com o nome de quem segura e o botão de reatribuir, não um alerta que some.
    if (codigoDoErro(e) === 'inbox_bound_to_other_bot') {
      conflito.value = {
        inboxId,
        mensagem: mensagemDoErro(e, t, t('POWER_AGENTS.CHANNEL.ERROR')),
        // Tirar a caixa de um robô que não é agente é do super admin (o servidor recusa com 403);
        // de outro agente da conta, o administrador tira.
        podeReatribuir:
          isSuperAdmin.value || Boolean(dadosDoErro(e).bound_agent_id),
      };
    } else {
      useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.CHANNEL.ERROR')));
    }
  } finally {
    connecting.value = false;
  }
}

// A inbox nasce pelo caminho do Embedded Signup (o backend já criou o canal e assinou os
// webhooks); aqui só falta amarrá-la ao agente, que é o que esta tela existe para fazer.
// Mora DEPOIS do `connect` de propósito: antes dele o lint acusa uso antes da definição.
async function onEmbeddedSignupSuccess(inbox) {
  if (!inbox?.id) {
    useAlert(t('POWER_AGENTS.CHANNEL.EMBEDDED_SIGNUP_NO_INBOX'));
    return;
  }
  await store.dispatch('inboxes/get');
  await connect(inbox.id);
}

async function carregarNumerosProprios() {
  carregandoProprios.value = true;
  erroProprios.value = '';
  try {
    const data = await ownedNumbersApi.list(ctx.accountId());
    numerosProprios.value = data.numbers || [];
  } catch (e) {
    numerosProprios.value = [];
    // `not_configured` não é falha: é a configuração da plataforma faltando, e a mensagem
    // do servidor já diz qual chave. Mostrá-la crua é melhor que um "erro" genérico.
    erroProprios.value = mensagemDoErro(
      e,
      t,
      t('POWER_AGENTS.CHANNEL.OWNED_ERROR')
    );
  } finally {
    carregandoProprios.value = false;
  }
}

// Cria a inbox na conta desta tela e, no mesmo passo, liga ao agente. Era exatamente isso
// que o link antigo não fazia: ele levava para fora e deixava o vínculo por conta de quem
// lembrasse de voltar.
async function conectarNumeroProprio(numero) {
  conectandoNumero.value = numero.phone_number_id;
  try {
    const inbox = await ownedNumbersApi.connect(ctx.accountId(), {
      wabaId: numero.waba_id,
      phoneNumberId: numero.phone_number_id,
      phoneNumber: numero.phone_number,
      inboxName: numero.verified_name || numero.display_phone_number,
    });
    await store.dispatch('inboxes/get');
    await connect(inbox.id);
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.CHANNEL.OWNED_ERROR')));
  } finally {
    conectandoNumero.value = '';
  }
}

// Só busca quando o caminho é escolhido: a listagem bate na Graph API uma vez por WABA, e
// pagar isso em toda abertura da seção seria cobrar de quem nem vai usar.
watch(metodoWa, novo => {
  if (novo === 'proprios' && !numerosProprios.value.length) {
    carregarNumerosProprios();
  }
});

// Canal API: cria o inbox e liga na mesma ação. Não há nada a configurar além do nome —
// é HTTP puro, sem credencial de terceiro — e por isso ele é o caminho que faz um agente
// recém-criado poder ser testado no Playground no mesmo minuto.
const apiInboxName = ref('');

async function createApiAndConnect() {
  const nome = apiInboxName.value.trim();
  if (!nome) return;
  creating.value = true;
  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: nome,
      channel: { type: 'api', webhook_url: '' },
    });
    await connect(inbox.id);
    apiInboxName.value = '';
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.CHANNEL.ERROR')));
  } finally {
    creating.value = false;
  }
}

// Caixa de site: cria e liga na mesma ação, como o canal API. Nome, endereço e cor são o
// que o widget precisa para subir; título, saudação e pré-chat ficam nas configurações da
// caixa. A cor padrão é a da criação de caixa de site da tela de caixas.
const webForm = reactive({
  inboxName: '',
  websiteUrl: '',
  widgetColor: '#009CE0',
});

async function createWebAndConnect() {
  const nome = webForm.inboxName.trim();
  const endereco = webForm.websiteUrl.trim();
  if (!nome || !endereco) return;
  creating.value = true;
  try {
    // A coleta de e-mail nasce DESLIGADA. Ligada, que é o padrão da caixa de site, o widget
    // põe o cartão "Informe à equipe uma forma de contatá-lo", assinado "Robôs", acima da
    // primeira resposta do agente. O dono religa nas configurações da caixa.
    const inbox = await store.dispatch('inboxes/createWebsiteChannel', {
      name: nome,
      enable_email_collect: false,
      channel: {
        type: 'web_widget',
        website_url: endereco,
        widget_color: webForm.widgetColor,
      },
    });
    // No site o passo não termina no vínculo: falta colar o código da caixa no site. A tela
    // fica no painel do site, com o código da caixa nova à vista, em vez de voltar à grade
    // e obrigar a clicar em "Site" de novo para achá-lo.
    await connect(inbox.id, { ficar: true });
    webForm.inboxName = '';
    webForm.websiteUrl = '';
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.CHANNEL.ERROR')));
  } finally {
    creating.value = false;
  }
}

// ── Block C — create a WhatsApp Cloud inbox, then connect it ─────
async function createAndConnect() {
  if (!phoneValid.value) {
    phoneError.value = t('POWER_AGENTS.CHANNEL.PHONE_INVALID');
    return;
  }
  phoneError.value = '';
  creating.value = true;
  try {
    // Meta webhooks resolve the channel by E.164 with a leading '+' —
    // a number stored without it is silently ignored by the events job.
    const e164Phone = normalizedPhone.value;
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: whatsappForm.inboxName,
      channel: {
        type: 'whatsapp',
        phone_number: e164Phone,
        provider: 'whatsapp_cloud',
        provider_config: {
          api_key: whatsappForm.api_key,
          phone_number_id: whatsappForm.phone_number_id,
          business_account_id: whatsappForm.business_account_id,
        },
      },
    });
    await connect(inbox.id);
    whatsappForm.inboxName = '';
    whatsappForm.phone_number = '';
    whatsappForm.phone_number_id = '';
    whatsappForm.business_account_id = '';
    whatsappForm.api_key = '';
  } catch (e) {
    useAlert(
      mensagemDoErro(e, t, e?.message || t('POWER_AGENTS.CHANNEL.CREATE_ERROR'))
    );
  } finally {
    creating.value = false;
  }
}

onMounted(() => {
  fetchChannel();
  store.dispatch('inboxes/get');
});
</script>

<template>
  <SectionShell
    data-busca="channel"
    :title="t('POWER_AGENTS.CHANNEL.TITLE')"
    :description="t('POWER_AGENTS.CHANNEL.DESCRIPTION')"
    :saving="null"
  >
    <!-- Conectados -->
    <div class="flex flex-col gap-2">
      <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
        {{ t('POWER_AGENTS.CHANNEL.CONNECTED_TITLE') }}
      </p>

      <div
        v-if="!connectedInboxes.length && !loadingChannel"
        class="flex flex-col items-center gap-1 px-4 py-6 text-center border border-dashed rounded-xl border-n-weak"
      >
        <i class="text-lg i-lucide-unplug text-n-slate-9" />
        <p class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.CHANNEL.NONE_CONNECTED') }}
        </p>
      </div>

      <div
        v-for="inbox in connectedInboxes"
        :key="inbox.id"
        class="flex items-center gap-3 px-4 py-3 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <span
          class="flex items-center justify-center rounded-lg size-9 bg-n-alpha-2"
        >
          <i
            :class="[
              canalDe(inbox.channel_type).icone,
              canalDe(inbox.channel_type).cor,
            ]"
            class="text-base"
          />
        </span>
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ inbox.name }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ canalDe(inbox.channel_type).rotulo }}
          </span>
        </div>
        <Button
          variant="outline"
          color="ruby"
          size="sm"
          :label="t('POWER_AGENTS.CHANNEL.DISCONNECT')"
          :is-loading="disconnecting[inbox.id]"
          class="ml-auto shrink-0"
          @click="disconnect(inbox.id)"
        />
      </div>
    </div>

    <!-- Escolha do tipo -->
    <div v-if="!tipo" class="flex flex-col gap-2">
      <div>
        <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
          {{ t('POWER_AGENTS.CHANNEL.ADD_TITLE') }}
        </p>
        <p class="mt-0.5 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.CHANNEL.ADD_HINT') }}
        </p>
      </div>

      <div class="grid gap-3 sm:grid-cols-2">
        <button
          class="flex flex-col gap-1 p-4 text-left transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:border-n-brand"
          @click="tipo = 'whatsapp'"
        >
          <i class="text-lg i-lucide-message-circle text-[#25D366]" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.CHANNEL.TYPE_WHATSAPP') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.CHANNEL.TYPE_WHATSAPP_HINT') }}
          </span>
        </button>

        <button
          class="flex flex-col gap-1 p-4 text-left transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:border-n-brand"
          @click="tipo = 'web'"
        >
          <i class="text-lg i-lucide-globe text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.CHANNEL.TYPE_WEB') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.CHANNEL.TYPE_WEB_HINT') }}
          </span>
        </button>

        <!-- O canal API saiu de "em breve" porque ele era o único caminho para um agente
             recém-criado poder ser TESTADO. Sem ele, a única criação de inbox nesta tela era
             a do WhatsApp Cloud, que exige credencial da Meta — e o Playground abria com
             "conecte um inbox primeiro", apontando para uma porta que não existia na tela. -->
        <button
          class="flex flex-col gap-1 p-4 text-left transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:border-n-brand"
          @click="tipo = 'api'"
        >
          <i class="text-lg i-lucide-plug text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.CHANNEL.TYPE_API') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.CHANNEL.TYPE_API_HINT') }}
          </span>
        </button>

        <button
          v-if="temGoogleChat"
          class="flex flex-col gap-1 p-4 text-left transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:border-n-brand"
          @click="tipo = 'google_chat'"
        >
          <i class="text-lg i-woot-google-chat text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.CHANNEL.TYPE_GOOGLE_CHAT') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.CHANNEL.TYPE_GOOGLE_CHAT_HINT') }}
          </span>
        </button>
      </div>
    </div>

    <!-- Formulário do tipo escolhido -->
    <div v-else class="flex flex-col gap-4">
      <button
        class="flex items-center gap-1.5 text-xs w-fit text-n-slate-11 hover:text-n-slate-12"
        @click="tipo = null"
      >
        <i class="i-lucide-arrow-left" />
        {{ t('POWER_AGENTS.CHANNEL.BACK') }}
      </button>

      <!-- As inboxes DESTE canal, como cards, mais um card de adicionar. Ver o comentário
           de `TIPO_PARA_CANAL`: escolher a inbox deixou de ser um tipo irmão dos canais e
           passou a morar DENTRO do canal, que é onde a pergunta faz sentido. -->
      <div v-if="podeCriar || inboxesDoTipo.length" class="flex flex-col gap-2">
        <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
          {{ t('POWER_AGENTS.CHANNEL.PICK_INBOX') }}
        </p>

        <!-- Conflito: a inbox responde por outro agente. Fica na tela com o nome do dono e a
             saída (reatribuir), em vez de um alerta genérico que some em três segundos. -->
        <div
          v-if="conflito"
          class="flex flex-wrap items-center gap-3 px-3 py-2.5 border rounded-lg border-n-amber-6 bg-n-amber-2"
        >
          <i class="text-base i-lucide-triangle-alert text-n-amber-10" />
          <p class="flex-1 text-xs min-w-40 text-n-amber-11">
            {{ conflito.mensagem }}
            <template v-if="!conflito.podeReatribuir">
              {{ t('POWER_AGENTS.CHANNEL.REASSIGN_PLATFORM_ONLY') }}
            </template>
          </p>
          <Button
            v-if="conflito.podeReatribuir"
            :label="t('POWER_AGENTS.CHANNEL.REASSIGN')"
            :is-loading="connecting"
            amber
            sm
            @click="
              connect(conflito.inboxId, { force: true, ficar: ficaNoSite })
            "
          />
          <Button
            :label="t('POWER_AGENTS.CHANNEL.CANCEL')"
            slate
            link
            sm
            @click="conflito = null"
          />
        </div>

        <div class="grid gap-3 sm:grid-cols-2">
          <button
            v-for="inbox in inboxesDoTipo"
            :key="inbox.id"
            :disabled="connecting"
            class="flex flex-col gap-1 p-4 text-left transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:border-n-brand disabled:opacity-60"
            @click="connect(inbox.id, { ficar: ficaNoSite })"
          >
            <span class="flex items-center gap-2 min-w-0">
              <i
                :class="[
                  canalDe(inbox.channelType).icone,
                  canalDe(inbox.channelType).cor,
                ]"
                class="text-base shrink-0"
              />
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ inbox.name }}
              </span>
            </span>
            <span v-if="donoDaInbox(inbox)" class="text-xs text-n-amber-11">
              {{ donoDaInbox(inbox) }}
            </span>
            <span v-else class="text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.CHANNEL.INBOX_FREE') }}
            </span>
          </button>

          <button
            v-if="podeCriar"
            class="flex flex-col gap-1 p-4 text-left transition-colors border border-dashed rounded-xl bg-n-alpha-1 hover:border-n-brand"
            :class="criandoNovo ? 'border-n-brand' : 'border-n-weak'"
            @click="criandoNovo = true"
          >
            <i class="text-lg i-lucide-plus text-n-slate-11" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('POWER_AGENTS.CHANNEL.ADD_NEW') }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.CHANNEL.ADD_NEW_HINT') }}
            </span>
          </button>
        </div>

        <!-- IRMÃO do grid, e não um elo da cadeia `v-else`. Foi exatamente esse o defeito da
             versão anterior: o aviso entrou entre o `v-if` e o `v-else` e passou a SUBSTITUIR
             a lista sempre que havia uma inbox presa — a tela ficava só com o aviso. -->
        <p v-if="temInboxPresa" class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.CHANNEL.BOUND_HINT') }}
        </p>
      </div>

      <!-- Sem criação aqui, o tipo sem caixa livre ficaria em branco: diz onde ela nasce. -->
      <p
        v-if="tipo === 'google_chat' && !inboxesDoTipo.length"
        class="text-xs text-n-slate-10"
      >
        {{ t('POWER_AGENTS.CHANNEL.NO_GOOGLE_CHAT_INBOX') }}
      </p>

      <!-- WhatsApp Cloud -->
      <div
        v-if="tipo === 'whatsapp' && mostrandoCriacao"
        class="flex flex-col gap-5 p-5 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <span
            class="flex items-center justify-center rounded-lg size-10 bg-[#25D366]/10"
          >
            <i class="text-lg i-lucide-message-circle text-[#25D366]" />
          </span>
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('POWER_AGENTS.CHANNEL.TYPE_WHATSAPP') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.CHANNEL.TYPE_WHATSAPP_HINT') }}
            </p>
          </div>
        </div>

        <!-- Escolha do caminho ANTES do formulário, no mesmo espírito da escolha de tipo:
             dois caminhos empilhados na mesma tela foi o que fez o operador não achar um. -->
        <div v-if="metodosWaDisponiveis.length > 1" class="flex gap-2">
          <button
            v-for="m in metodosWaDisponiveis"
            :key="m.id"
            type="button"
            class="px-3 py-1.5 text-xs border rounded-lg"
            :class="
              metodoWa === m.id
                ? 'border-n-brand text-n-slate-12'
                : 'border-n-weak text-n-slate-11'
            "
            @click="metodoWa = m.id"
          >
            {{ m.rotulo }}
          </button>
        </div>

        <!-- Embedded Signup: o MESMO componente da tela nativa de inbox, com o redirect
             desligado para a inbox criada voltar para cá e já entrar ligada ao agente.
             Copiar o fluxo para cá criaria a segunda cópia de uma corrida delicada (o
             `code` do FB.login contra o `postMessage` da Meta chegam fora de ordem). -->
        <div v-if="metodoWa === 'meta'" class="max-w-xl">
          <WhatsappEmbeddedSignup
            :redirect-on-success="false"
            @success="onEmbeddedSignupSuccess"
          />
        </div>

        <!-- Números da Powerbizz: a lista que antes só existia no Administrate. Um clique
             cria a inbox NESTA conta e já a liga ao agente. -->
        <div v-if="metodoWa === 'proprios'" class="flex flex-col gap-3">
          <p v-if="carregandoProprios" class="text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.CHANNEL.OWNED_LOADING') }}
          </p>

          <p v-else-if="erroProprios" class="text-xs text-n-ruby-10">
            {{ erroProprios }}
          </p>

          <p
            v-else-if="!numerosProprios.length"
            class="text-xs text-n-slate-10"
          >
            {{ t('POWER_AGENTS.CHANNEL.OWNED_EMPTY') }}
          </p>

          <div v-else class="flex flex-col gap-2">
            <button
              v-for="n in numerosProprios"
              :key="n.phone_number_id"
              type="button"
              :disabled="!n.selectable || !!conectandoNumero"
              class="flex items-center gap-3 p-3 text-left border rounded-lg border-n-weak bg-n-background disabled:opacity-60"
              :class="
                n.selectable ? 'hover:border-n-brand' : 'cursor-not-allowed'
              "
              @click="conectarNumeroProprio(n)"
            >
              <i class="text-base i-lucide-message-circle text-[#25D366]" />
              <span class="flex flex-col min-w-0">
                <span class="text-sm truncate text-n-slate-12">
                  {{ n.verified_name || n.display_phone_number }}
                </span>
                <span class="font-mono text-xs text-n-slate-10">
                  {{ n.display_phone_number }}
                </span>
                <!-- Por que NÃO dá para escolher, quando não dá. Sem isto a linha some
                     ou fica cinza sem explicação, e o operador tenta de novo. -->
                <span v-if="n.blocked_reason" class="text-xs text-n-slate-10">
                  {{ n.blocked_reason }}
                </span>
              </span>
              <span class="ml-auto text-xs shrink-0 text-n-slate-10">
                <!-- LIVE quer dizer que uma mensagem daqui chega numa pessoa de verdade. -->
                {{
                  n.live
                    ? t('POWER_AGENTS.CHANNEL.OWNED_LIVE')
                    : t('POWER_AGENTS.CHANNEL.OWNED_NOT_LIVE')
                }}
              </span>
            </button>
          </div>
        </div>

        <div v-if="metodoWa === 'manual'" class="flex flex-col gap-3">
          <p
            class="text-xs font-medium tracking-wide uppercase text-n-slate-11"
          >
            {{ t('POWER_AGENTS.CHANNEL.WHATSAPP_NUMBER_GROUP') }}
          </p>
          <div class="grid gap-3 sm:grid-cols-2">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">
                {{ t('POWER_AGENTS.CHANNEL.INBOX_NAME') }}
              </label>
              <input
                v-model="whatsappForm.inboxName"
                type="text"
                autocomplete="off"
                class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">
                {{ t('POWER_AGENTS.CHANNEL.PHONE_NUMBER') }}
              </label>
              <input
                v-model="whatsappForm.phone_number"
                type="text"
                autocomplete="off"
                :placeholder="
                  t('POWER_AGENTS.CHANNEL.PHONE_NUMBER_PLACEHOLDER')
                "
                class="w-full px-3 py-2 text-sm border rounded-lg outline-none bg-n-background text-n-slate-12 focus:border-n-brand"
                :class="phoneError ? 'border-n-ruby-8' : 'border-n-weak'"
                @input="phoneError = ''"
              />
              <p v-if="phoneError" class="text-xs text-n-ruby-10">
                {{ phoneError }}
              </p>
              <p v-else class="text-xs text-n-slate-9">
                {{ t('POWER_AGENTS.CHANNEL.PHONE_HINT') }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="metodoWa === 'manual'" class="flex flex-col gap-3">
          <div>
            <p
              class="text-xs font-medium tracking-wide uppercase text-n-slate-11"
            >
              {{ t('POWER_AGENTS.CHANNEL.WHATSAPP_META_GROUP') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.CHANNEL.WHATSAPP_META_HINT') }}
            </p>
          </div>
          <div class="grid gap-3 sm:grid-cols-2">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">
                {{ t('POWER_AGENTS.CHANNEL.PHONE_NUMBER_ID') }}
              </label>
              <input
                v-model="whatsappForm.phone_number_id"
                type="text"
                autocomplete="off"
                class="w-full px-3 py-2 font-mono text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">
                {{ t('POWER_AGENTS.CHANNEL.BUSINESS_ACCOUNT_ID') }}
              </label>
              <input
                v-model="whatsappForm.business_account_id"
                type="text"
                autocomplete="off"
                class="w-full px-3 py-2 font-mono text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
              />
            </div>
          </div>
          <!--
            `new-password`, e nao `off`: este campo ja tinha `off` desde 06/09 e o
            gerenciador de senhas preencheu assim mesmo no teste de 15/09 (o par
            e-mail/senha caiu em "Business Account ID" e "Access token"). Em campo
            `type="password"` o `off` e rotineiramente ignorado; `new-password` e o
            valor que diz ao gerenciador que aqui nao vai o login salvo dele.
            Credencial errada aqui nao para na tela: vira canal criado com lixo e
            registro na Meta com a senha do operador.
          -->
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">
              {{ t('POWER_AGENTS.CHANNEL.API_KEY') }}
            </label>
            <input
              v-model="whatsappForm.api_key"
              type="password"
              autocomplete="new-password"
              class="w-full px-3 py-2 font-mono text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            />
          </div>
        </div>

        <Button
          v-if="metodoWa === 'manual'"
          size="sm"
          icon="i-lucide-plug-zap"
          :label="t('POWER_AGENTS.CHANNEL.CREATE_CONNECT')"
          :is-loading="creating"
          :disabled="!phoneValid"
          class="self-start"
          @click="createAndConnect"
        />
      </div>

      <!-- Canal API -->
      <div
        v-else-if="tipo === 'api' && mostrandoCriacao"
        class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <i class="text-lg i-lucide-plug text-n-slate-11" />
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('POWER_AGENTS.CHANNEL.TYPE_API') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.CHANNEL.API_CREATE_HINT') }}
            </p>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11">
            {{ t('POWER_AGENTS.CHANNEL.INBOX_NAME') }}
          </label>
          <input
            v-model="apiInboxName"
            type="text"
            :placeholder="t('POWER_AGENTS.CHANNEL.API_NAME_PLACEHOLDER')"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
        </div>

        <Button
          size="sm"
          icon="i-lucide-plug-zap"
          :label="t('POWER_AGENTS.CHANNEL.CREATE_CONNECT')"
          :is-loading="creating"
          :disabled="!apiInboxName.trim()"
          class="self-start"
          @click="createApiAndConnect"
        />
      </div>

      <!-- Caixa de site nova -->
      <div
        v-else-if="tipo === 'web' && mostrandoCriacao"
        class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <i class="text-lg i-lucide-globe text-n-slate-11" />
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('POWER_AGENTS.CHANNEL.WEB_CREATE') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.CHANNEL.WEB_CREATE_HINT') }}
            </p>
          </div>
        </div>

        <div class="grid gap-3 sm:grid-cols-2">
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">
              {{ t('POWER_AGENTS.CHANNEL.INBOX_NAME') }}
            </label>
            <input
              v-model="webForm.inboxName"
              type="text"
              :placeholder="t('POWER_AGENTS.CHANNEL.WEB_NAME_PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none reset-base border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">
              {{ t('POWER_AGENTS.CHANNEL.WEB_URL') }}
            </label>
            <input
              v-model="webForm.websiteUrl"
              type="text"
              :placeholder="t('POWER_AGENTS.CHANNEL.WEB_URL_PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none reset-base border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            />
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11">
            {{ t('POWER_AGENTS.CHANNEL.WEB_COLOR') }}
          </label>
          <input
            v-model="webForm.widgetColor"
            type="color"
            class="w-16 h-9 p-0.5 border rounded-lg cursor-pointer border-n-weak bg-n-background"
          />
        </div>

        <Button
          size="sm"
          icon="i-lucide-plug-zap"
          :label="t('POWER_AGENTS.CHANNEL.CREATE_CONNECT')"
          :is-loading="creating"
          :disabled="!webForm.inboxName.trim() || !webForm.websiteUrl.trim()"
          class="self-start"
          @click="createWebAndConnect"
        />
      </div>

      <!-- Widget do site: um bloco por caixa de site ligada a este agente, cada um com o
           código DAQUELA caixa e o seu botão de copiar (o do `woot-code`, o mesmo do fim da
           criação de caixa). Com duas caixas, são dois códigos: cada site tem o seu token.
           Fica FORA da cadeia `v-else-if` dos formulários: criando uma segunda caixa, o
           código da primeira continua à vista. Sem caixa ligada e com o formulário aberto,
           ele não tem o que mostrar e sai. -->
      <div
        v-if="tipo === 'web' && (caixasDeSite.length || !mostrandoCriacao)"
        class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <span
            class="flex items-center justify-center rounded-lg size-10 bg-n-alpha-2"
          >
            <i class="text-lg i-lucide-globe text-n-slate-11" />
          </span>
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('POWER_AGENTS.CHANNEL.TYPE_WEB') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{
                caixasDeSite.length
                  ? t('POWER_AGENTS.CHANNEL.WEB_HINT')
                  : t('POWER_AGENTS.CHANNEL.WEB_NONE')
              }}
            </p>
          </div>
        </div>

        <div
          v-for="caixa in caixasDeSite"
          :key="caixa.id"
          class="flex flex-col gap-2"
        >
          <p class="text-xs font-medium text-n-slate-11">
            {{ caixa.name }}
          </p>
          <woot-code v-if="caixa.script" :script="caixa.script" />
        </div>

        <!-- Sem caixa de site não há código a mostrar: o widget só sobe com o token de uma.
             O caminho é criar a caixa aqui mesmo, já ligada ao agente. -->
        <Button
          v-if="!caixasDeSite.length && podeCriar"
          size="sm"
          icon="i-lucide-plus"
          :label="t('POWER_AGENTS.CHANNEL.WEB_CREATE')"
          class="self-start"
          @click="criandoNovo = true"
        />
      </div>

      <!-- Inbox que já existe na conta -->
    </div>

    <!-- O link de teste: uma página pública para conversar com a versão publicada do agente.
         Fora da escolha de tipo porque não é caixa de entrada. É da plataforma por inteiro
         (`PowerAgents::TestLinksController`), e some para o administrador, que vê esta seção
         desde 29/09/2026 para ligar e desligar caixa. -->
    <TestLinkBlock v-if="isSuperAdmin" />
  </SectionShell>
</template>
